import {
  Alert,
  Box,
  Button,
  Divider,
  Heading,
  Link,
  Modal,
  ModalBody,
  Text,
  TextArea,
  TextInput,
} from '@razorpay/blade/components';
import { routePaths } from 'components/Routes/data';
import { salaryComponentParams } from 'components/SalaryComponents/constants';
import { useComponentParams, useDetailsView } from 'components/SalaryComponents/hooks';
import { knowMoreItems } from 'components/WizardViews/SalaryComponents/constants';
import { NonPayableBenefitsFormData } from 'components/WizardViews/SalaryComponents/types';
import {
  FieldGroup,
  FormWrapper,
  StepActionsWrapper,
  StepContentWrapper,
  StepHeader,
  SwitchDescription,
  SwitchField,
} from 'components/WizardViews/components';
import { ValidationSchema, useForm } from 'forms';
import React, { FormEvent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

// Validation schema for edit mode
const editValidationSchema: ValidationSchema<NonPayableBenefitsFormData> = {
  displayName: (formData) => {
    if (!formData.displayName) return 'Display name is required';
    return null;
  },
  description: () => null,
};

const dummyFormData: NonPayableBenefitsFormData = {
  name: 'Medical Insurance',
  description: 'Acko general insurance for all full-time employees',
  source: 'USER',
  displayName: 'Medical Insurance',
  isPartOfCTC: true,
  visibilityToEmployee: true,
};

const GeneralDetailsStep: React.FC = () => {
  const { formData, validate, register, setFormData } = useForm<NonPayableBenefitsFormData>();

  useEffect(() => {
    // Temporarily adding dummy form data until integration is done
    if (!formData?.name) {
      setFormData(dummyFormData);
    }
  }, [formData, setFormData]);

  const history = useHistory();
  const { openDetailsView } = useDetailsView(salaryComponentParams.knowMore);
  const { searchString } = useComponentParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (validate(editValidationSchema)) {
      setIsModalOpen(true);
    }
  };

  const handleConfirm = () => {
    setIsSubmitting(true);
    // Here you would typically make an API call to update the component
    setTimeout(() => {
      setIsSubmitting(false);
      setIsModalOpen(false);
      history.push({
        pathname: routePaths.salaryComponents.root,
        search: searchString,
      });
    }, 1000);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  // Determine if the component is already part of a salary structure
  // This is used in the UI to show appropriate messages

  const alertDescription = (
    <>
      1. If display name is updated, past salary registers and payslips will reflect the new Display
      Name.
      <br />
      2. "Part of salary structure" setting can't be updated. Create a new component to make
      changes.
    </>
  );

  return (
    <FormWrapper onSubmit={handleSubmit}>
      <StepContentWrapper>
        <StepHeader title="General" subtitle="Configuration" marginBottom={'spacing.9'}/>

        <Alert
          marginBottom="spacing.7"
          isFullWidth
          title="Edit details"
          isDismissible={false}
          description={alertDescription}
        />

        <FieldGroup title="General details">
          <TextInput
            label="Component name"
            placeholder="Enter component name"
            {...register('name')}
            isDisabled
          />

          <TextInput
            label="Display name in payslip & Salary Register"
            placeholder="Enter display name"
            helpText="Display will be used in employee payslip and salary register"
            {...register('displayName')}
            isRequired
          />

          <TextArea
            label="Component description"
            placeholder="Enter description"
            {...register('description')}
          />
        </FieldGroup>

        <Divider marginY="spacing.8" />

        <FieldGroup title="Part of salary structure">
          <SwitchField
            label="Component can be part of salary structure"
            accessibilityLabel="Include in CTC"
            size="small"
            {...register('isPartOfCTC', 'switch')}
            isDisabled={true}
            >
            <SwitchDescription content="Use component to add to a salary structure and assign it to employees." />
            <Link
              variant={'button'}
              marginLeft="spacing.2"
              size="small"
              onClick={() => openDetailsView(knowMoreItems.proration)}
              accessibilityLabel={'know more - proration'}>
              Know more✨
            </Link>
          </SwitchField>
        </FieldGroup>

        <Divider marginY="spacing.8" />

        <FieldGroup title="Employee visibility">
          <SwitchField
            label="Visible to employee as part of their compensation"
            accessibilityLabel="Visible to employee"
            size="small"
            tooltip={
              formData.isPartOfCTC
                ? 'The component that is part of the CTC will always be visible to employees on their compensation'
                : ''
            }
            {...register('visibilityToEmployee', 'switch')}
            isDisabled={formData.isPartOfCTC}
            isChecked={formData.isPartOfCTC ? true : formData.visibilityToEmployee}>
            <SwitchDescription content="Component will be visible to employee in their compensation." />
            <Link
              variant={'button'}
              marginLeft="spacing.2"
              size="small"
              onClick={() => openDetailsView(knowMoreItems.proration)}
              accessibilityLabel={'know more - proration'}>
              Know more✨
            </Link>
          </SwitchField>
        </FieldGroup>
      </StepContentWrapper>

      <Modal
        isOpen={isModalOpen}
        onDismiss={handleCancel}
        size="small"
        accessibilityLabel="Confirm changes">
        <ModalBody padding="spacing.6">
          <Heading size="large">Confirm changes</Heading>
          <Text weight="regular" marginY="spacing.4">
            The changes to the Non-Payable benefit component "{formData.name}" will be saved.
          </Text>
          <Box display="flex" justifyContent="flex-end" gap="spacing.3">
            <Button variant="tertiary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} isLoading={isSubmitting}>
              Confirm changes
            </Button>
          </Box>
        </ModalBody>
      </Modal>

      <StepActionsWrapper>
        <span />
        <Button type="submit">Confirm changes</Button>
      </StepActionsWrapper>
    </FormWrapper>
  );
};

export default GeneralDetailsStep;
